import { FC } from 'react';
import dayjs from 'dayjs';
import { useCallInsightController } from '@frontend/call-insight-panel';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { taskCenterTrackingIds } from '@frontend/task-center-components';
import type { Citation } from '@frontend/task-center-scope';
import { Text, Avatar, Button, SpinningLoader, NakedButton } from '@frontend/design-system';
import { useTaskFormPatient, useGetUserFromUserId } from '../../../hooks';
import { getCitationTitle } from '../../../utils';
import { citationContainerStyle, messageStyle, alignRight, noCitationContainerStyle, eggplantBorder } from './styles';

interface CallIntelCitationProps {
  locationId: string;
  sourceId?: string;
  citation?: Citation;
  taskId: string;
}

export const CallIntelCitation: FC<CallIntelCitationProps> = ({ citation, sourceId, locationId, taskId }) => {
  const { t } = useTranslation('task-center');

  const { open } = useCallInsightController({ source: 'taskCenter' });

  const { patient, isLoading: isLoadingPatient } = useTaskFormPatient(citation?.patient_id);
  const { data: user, isLoading: isLoadingUser } = useGetUserFromUserId(citation?.user_id, !citation?.patient_id);
  const date = citation?.created_at ? dayjs(citation?.created_at).format('MMM DD, YYYY, hh:mm A') : '';
  const title = getCitationTitle({ patient, user, phone: citation?.phone_no });

  const isFetching = isLoadingPatient || isLoadingUser;

  const onClickView = async () => {
    if (sourceId) open({ channelId: sourceId, locationId, includeCallIntelligence: true }, { taskId });
  };

  if (!citation) {
    return (
      <div css={[noCitationContainerStyle, eggplantBorder]}>
        <Icon name='phone-incoming-small' size={16} />
        <Text size='large' weight='bold'>
          {t('View Details in Call Intel')}
        </Text>
        <Button
          variant='tertiary'
          css={alignRight}
          onClick={onClickView}
          trackingId={taskCenterTrackingIds.openCallIntelPanel}
        >
          {t('View')}
        </Button>
      </div>
    );
  }

  return (
    <NakedButton
      css={[citationContainerStyle, eggplantBorder]}
      onClick={onClickView}
      trackingId={taskCenterTrackingIds.openCallIntelPanelWithCitation}
    >
      <span>
        <Icon name='phone-incoming-small' size={16} />
        <Text size='large' weight='bold'>
          {t('Call Intelligence')}
        </Text>
      </span>
      <span>
        {isFetching ? (
          <SpinningLoader />
        ) : (
          <>
            <Avatar
              name={user || patient ? title : undefined}
              isUser={!!user}
              size='small'
              disableHoverLabel={!citation?.phone_no}
            />
            {title && (
              <Text size='medium' weight='bold' textAlign='left'>
                {title}
              </Text>
            )}
          </>
        )}
        <Text color='light' size='small' css={alignRight}>
          {date}
        </Text>
      </span>
      <span>
        {citation?.description && (
          <Text size='large' css={messageStyle}>
            {citation?.description}
          </Text>
        )}
      </span>
    </NakedButton>
  );
};
